/// <amd-module name="Comments/Redux/CommentsActions" />
import { IComment, ICommentArtifactInfo, ICommentPage, ICommentReaction, CommentReactionType } from "Comments/Common/Contracts";
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";

/**
 * Action types.
 */
export const enum CommentsActionTypes {
    // for reducer
    Load = "Comments/Load",
    AddList = "Comments/AddList",
    AddPage = "Comments/AddPage",
    StartEditItem = "Comments/Item/StartEdit",
    CancelEditItem = "Comments/Item/CancelEdit",
    AddItem = "Comments/Item/Add",
    UpdateItem = "Comments/Item/Update",
    DeleteItem = "Comments/Item/Delete",
    UpdateReaction = "Comments/Reaction/Update",
    PendingReactionActionCallsInitiated = "Comments/Reaction/Update/PendingReactionActionCallsInitiated",
    AddRecentlyReactedUsers = "Comments/Reaction/AddRecentlyReactedUsers",

    // for saga
    StartUpdateItem = "Comments/Item/StartUpdate",
    StartDeleteItem = "Comments/Item/StartDelete",
    StartGetItem = "Comments/Item/StartGet",
    StartUpdatePendingReactions = "Comments/Reaction/StartUpdatePendingReactions",
    StartUpdateReaction = "Comments/Reaction/StartUpdateReaction",
    StartGetRecentlyReactedUsers = "Comments/Reaction/StartGetRecentlyReactedUsers"
}

/**
 * Actions for Comments.
 */
export const CommentsActions = {
    /**
     * Load a new set of comments for a specific artifact. If there is an existing set, they are replaced (to append, use addComments or addPage instead).
     */
    load: (artifactInfo: ICommentArtifactInfo, comments: IComment[]) => createAction(CommentsActionTypes.Load, { artifactInfo, comments }),

    /**
     * Add a new set of comments for a specific artifact.
     */
    addList: (artifactInfo: ICommentArtifactInfo, comments: IComment[]) => createAction(CommentsActionTypes.AddList, { artifactInfo, comments }),

    /**
     * Add a new page of comments for a specific artifact.
     */
    addPage: (artifactInfo: ICommentArtifactInfo, commentPage: ICommentPage) =>
        createAction(CommentsActionTypes.AddPage, { artifactInfo, commentPage }),

    /**
     * Start editing a comment.
     */
    startEditItem: (artifactInfo: ICommentArtifactInfo, commentId: number) =>
        createAction(CommentsActionTypes.StartEditItem, { artifactInfo, commentId }),

    /**
     * Cancel editing of a comment.
     */
    cancelEditItem: (artifactInfo: ICommentArtifactInfo, commentId: number) =>
        createAction(CommentsActionTypes.CancelEditItem, { artifactInfo, commentId }),

    /**
     * Add a new comment and its editing state for a specific artifact.
     */
    addItem: (artifactInfo: ICommentArtifactInfo, comment: IComment, isEditing: boolean = false) =>
        createAction(CommentsActionTypes.AddItem, { artifactInfo, comment, isEditing }),

    /**
     * Update a comment in the state.
     */
    updateItem: (artifactInfo: ICommentArtifactInfo, comment: IComment) => createAction(CommentsActionTypes.UpdateItem, { artifactInfo, comment }),

    /**
     * Delete a comment in the state.
     */
    deleteItem: (artifactInfo: ICommentArtifactInfo, commentId: number) => createAction(CommentsActionTypes.DeleteItem, { artifactInfo, commentId }),

    /**
     * Update isPendingReactionActionUpdatesInitiated flag state
     */
    pendingReactionActionCallsInitiated: (artifactInfo: ICommentArtifactInfo, commentId: number) =>
        createAction(CommentsActionTypes.PendingReactionActionCallsInitiated, { artifactInfo, commentId }),

    /**
     * Updates a comment reaction in the state when a user engages/disengages on a reaction
     */
    updateReaction: (
        artifactInfo: ICommentArtifactInfo,
        updateReactionResponse: ICommentReaction,
        currentUser: IdentityRef,
        isAdded: boolean,
        isLocalUpdate: boolean
    ) => createAction(CommentsActionTypes.UpdateReaction, { artifactInfo, updateReactionResponse, currentUser, isAdded, isLocalUpdate }),

    /**
     * Updates the list of recently reacted users in the state for a reaction
     */
    addRecentlyReactedUsers: (
        artifactInfo: ICommentArtifactInfo,
        commentId: number,
        reactionType: CommentReactionType,
        recentEngagedUsers: IdentityRef[]
    ) => createAction(CommentsActionTypes.AddRecentlyReactedUsers, { artifactInfo, commentId, reactionType, recentEngagedUsers }),

    /**
     * Start the update of a comment. This action is for the saga.
     */
    startUpdateItem: (artifactInfo: ICommentArtifactInfo, commentId: number, newValue: string) =>
        createAction(CommentsActionTypes.StartUpdateItem, { artifactInfo, commentId, newValue }),

    /**
     * Start deletion of a comment. This action is for the saga.
     */
    startDeleteItem: (artifactInfo: ICommentArtifactInfo, commentId: number) =>
        createAction(CommentsActionTypes.StartDeleteItem, { artifactInfo, commentId }),

    /**
     * Start get of a comment; this can be used to get a comment not yet in the store, or refresh it. This action is for the saga.
     */
    startGetItem: (artifactInfo: ICommentArtifactInfo, commentId: number, isEditing?: boolean) =>
        createAction(CommentsActionTypes.StartGetItem, { artifactInfo, commentId, isEditing }),

    /**
     * Start running pending engage or disengage action for all reaction types. Reaction types with no pending
     * action will NOT be included in the map. True in map's value indicates pending engage action and false for
     * disengage
     */
    startUpdatePendingCommentReactions: (
        artifactInfo: ICommentArtifactInfo,
        commentId: number,
        reactionTypePendingActionMap: { [reactionType: number]: boolean }
    ) => createAction(CommentsActionTypes.StartUpdatePendingReactions, { artifactInfo, commentId, reactionTypePendingActionMap }),

    /**
     * Start updating comment reaction based on whether user engaged or disengaged on a reaction type.
     * This action first updates the reaction info state immediately before receiving the response
     * from the server to keep the reactions experience slick
     */
    startUpdateCommentReaction: (
        artifactInfo: ICommentArtifactInfo,
        commentId: number,
        reactionType: CommentReactionType,
        isAdded: boolean,
        updateLocalStoreFirst: boolean
    ) => createAction(CommentsActionTypes.StartUpdateReaction, { artifactInfo, commentId, reactionType, isAdded, updateLocalStoreFirst }),

    /**
     * Start get most recently reacted users for a given reaction type
     */
    startGetRecentlyReactedUsers: (artifactInfo: ICommentArtifactInfo, commentId: number, reactionType: CommentReactionType, top: number) =>
        createAction(CommentsActionTypes.StartGetRecentlyReactedUsers, { artifactInfo, commentId, reactionType, top })
};

export type CommentsActions = ActionsUnion<typeof CommentsActions>;
